/**
 * Friendly labels for the stack values a project or an onboarding answer
 * can carry. Single source of truth — `StepStack.tsx` (onboarding's
 * multi-select) and `SettingsTab.tsx` (the project's single-value stack
 * pickers) both render from this list rather than keeping their own
 * copies of the same value → label mapping. See
 * feature-specs/webportal-ux/UX-06-settings-form.md.
 */

export interface StackOption {
  value: string;
  label: string;
}

export interface StackSlotOptions {
  slot: 'backend' | 'web' | 'mobile' | 'windows' | 'database';
  label: string;
  options: StackOption[];
}

export const STACK_OPTIONS: StackSlotOptions[] = [
  {
    slot: 'backend',
    label: 'Backend',
    options: [
      { value: 'node', label: 'Node.js' },
      { value: 'dotnet', label: '.NET (modern)' },
      { value: 'dotnet-framework', label: '.NET Framework (classic)' },
      { value: 'java', label: 'Java (Spring Boot)' },
      { value: 'python', label: 'Python (FastAPI/Django)' },
      { value: 'none', label: 'None' },
    ],
  },
  {
    slot: 'web',
    label: 'Web',
    options: [
      { value: 'react', label: 'React' },
      { value: 'react-vite', label: 'React + Vite' },
      { value: 'nextjs', label: 'Next.js' },
      { value: 'angular', label: 'Angular' },
      { value: 'blazor', label: 'Blazor' },
      { value: 'none', label: 'None' },
    ],
  },
  {
    slot: 'mobile',
    label: 'Mobile',
    options: [
      { value: 'flutter', label: 'Flutter' },
      { value: 'unity', label: 'Unity' },
      { value: 'react-native', label: 'React Native' },
      { value: 'ios-swift', label: 'iOS (Swift)' },
      { value: 'none', label: 'None' },
    ],
  },
  {
    slot: 'windows',
    label: 'Windows desktop',
    options: [
      { value: 'csharp', label: 'C# (WPF/WinForms/MAUI)' },
      { value: 'none', label: 'None' },
    ],
  },
  {
    slot: 'database',
    label: 'Database',
    options: [
      { value: 'ssdt', label: 'SQL Server (SSDT)' },
      { value: 'none', label: 'None' },
    ],
  },
];

/** The option list for one stack slot, or `[]` for an unknown slot. */
export function stackOptionsFor(slot: string): StackOption[] {
  return STACK_OPTIONS.find((s) => s.slot === slot)?.options ?? [];
}

/** Friendly label for a stack value in a given slot; falls back to the raw value. */
export function stackLabel(slot: string, value: string): string {
  return stackOptionsFor(slot).find((o) => o.value === value)?.label ?? value;
}
